import { normalizeBars } from './bars';
import { buildTimeframes } from './timeframes';
import { tagsLimit, limitFillPrice, resolveStopPrice } from './fillModel';
import {
  addToPosition,
  applyBarFills,
  closePosition,
  finalizeTrade,
  openPosition,
  pnlFor,
  reducePosition,
  snapshotPosition,
} from './positionMachine';
import {
  DEFAULT_BACKTEST_QTY,
  DEFAULT_MAX_RISK,
  DEFAULT_POSITION_MODE,
  DEFAULT_RISK_PERCENT,
  DEFAULT_RISK_PERCENT_BASIS,
  DEFAULT_SIZE_MODE,
  DEFAULT_STARTING_BALANCE,
  isPositionMode,
  isRiskPercentBasis,
  isSizeMode,
  parsePositiveNumber,
  qtyFromFixed,
  qtyFromRisk,
  riskCapFromPercent,
  wholeQty,
} from './positionMode';
import { buildEquity, computeStats } from './stats';
import { paramsFromSchema } from '../strategies/params';
import { barInTradeSession, tradeSessionActive } from './tradeSession';

export function pendingEntryExpired(action, sessionBar) {
  if (!action) return false;
  if (action.expireDateKey || action.expireAtMinutes != null) {
    if (!sessionBar || sessionBar.hour == null || sessionBar.minute == null) return false;
    if (action.expireDateKey && sessionBar.dateKey && sessionBar.dateKey !== action.expireDateKey) return true;
    if (action.expireAtMinutes != null) {
      return sessionBar.hour * 60 + sessionBar.minute >= action.expireAtMinutes;
    }
  }
  if (action.expireSessionKey) {
    return !sessionBar || sessionBar.sessionKey !== action.expireSessionKey;
  }
  return false;
}

function sliceInd(ind, i) {
  if (!ind || typeof ind !== 'object') return {};
  const out = {};
  for (const [key, series] of Object.entries(ind)) {
    out[key] = Array.isArray(series) ? series : series;
    out[`${key}Now`] = Array.isArray(series) ? series[i] : series;
  }
  return out;
}

export function runBacktest({
  bars: rawBars,
  strategy,
  params: paramOverrides = {},
  qty = DEFAULT_BACKTEST_QTY,
  unit = 'Minute',
  positionMode = DEFAULT_POSITION_MODE,
  sizeMode = DEFAULT_SIZE_MODE,
  maxRisk = DEFAULT_MAX_RISK,
  startingBalance = DEFAULT_STARTING_BALANCE,
  riskPercent = DEFAULT_RISK_PERCENT,
  riskPercentBasis = DEFAULT_RISK_PERCENT_BASIS,
  pointValue = 1,
  tradeSession = null,
  timeframes = {},
} = {}) {
  if (!strategy || typeof strategy.onBar !== 'function') {
    throw new Error('runBacktest requires a strategy with onBar()');
  }
  const mode = isPositionMode(positionMode) ? positionMode : DEFAULT_POSITION_MODE;
  const sizing = isSizeMode(sizeMode) ? sizeMode : DEFAULT_SIZE_MODE;
  const defaultQty = qtyFromFixed(qty);
  const riskCap = parsePositiveNumber(maxRisk, DEFAULT_MAX_RISK);
  const startBal = parsePositiveNumber(startingBalance, DEFAULT_STARTING_BALANCE);
  const pct = parsePositiveNumber(riskPercent, DEFAULT_RISK_PERCENT);
  const pctBasis = isRiskPercentBasis(riskPercentBasis) ? riskPercentBasis : DEFAULT_RISK_PERCENT_BASIS;
  const pv = Number(pointValue) > 0 ? Number(pointValue) : 1;
  const filterEntries = tradeSessionActive(tradeSession);
  const bars = normalizeBars(rawBars, unit);
  const params = paramsFromSchema(strategy.paramSchema, paramOverrides);

  if (Array.isArray(strategy.requiredTimeframes)) {
    for (const req of strategy.requiredTimeframes) {
      const htf = timeframes ? timeframes[req.key] : null;
      if (!Array.isArray(htf) || htf.length === 0) {
        throw new Error(`missing timeframe ${req.key}`);
      }
    }
  }
  const tf = buildTimeframes(bars, timeframes, strategy.requiredTimeframes);

  const prepared = typeof strategy.prepare === 'function'
    ? (strategy.prepare(bars, params, tf) || {})
    : {};
  const state = prepared.state || {};
  const session = prepared.session || null;

  let position = null;
  let pending = [];
  const trades = [];
  const workingOrders = [];
  const barCriteria = new Array(bars.length);
  let nextId = 1;
  let nextWorkingId = 1;
  let cash = startBal;

  const fillPriceFor = (action, bar, barIndex) => {
    if ((action.orderType || 'market') === 'limit') {
      if (Number.isFinite(action.placedIndex) && barIndex < action.placedIndex) return null;
      if (!tagsLimit(bar, action.side, action.limitPrice)) return null;
      return limitFillPrice(bar, action.side, action.limitPrice);
    }
    // Market entries fill on the next bar's open (signal is evaluated at bar close).
    if (Number.isFinite(action.placedIndex) && barIndex <= action.placedIndex) return null;
    return bar.open;
  };

  const markEquity = (markPrice) => {
    if (!position || position.status === 'closed') return cash;
    const qty = Number(position.remainingQty);
    if (!(qty > 0)) return cash;
    return cash + pnlFor(position.side, position.entryPrice, markPrice, qty, position.pointValue);
  };

  const resolveFillQty = (action, fillPrice) => {
    if (Number(action.qty) > 0) return wholeQty(action.qty);
    if (sizing === 'risk' || sizing === 'percent') {
      const stop = resolveStopPrice(action.side, fillPrice, action);
      const cap = sizing === 'percent'
        ? riskCapFromPercent({
          percent: pct,
          basis: pctBasis,
          startingBalance: startBal,
          equity: markEquity(fillPrice),
        })
        : riskCap;
      return qtyFromRisk({
        maxRisk: cap,
        entryPrice: fillPrice,
        stopPrice: stop,
        pointValue: pv,
      });
    }
    return defaultQty;
  };

  const rejectOrder = (action, barIndex, reason) => {
    action.expireIndex = barIndex;
    action.rejectReason = reason;
  };

  const openFromAction = (action, bar, i, fillPrice, fillQty) => {
    position = openPosition({
      id: nextId++,
      side: action.side,
      qty: fillQty,
      entryPrice: fillPrice,
      entryIndex: i,
      entryTime: bar.timestamp,
      stopPrice: action.stopPrice,
      atr: action.atr,
      atrMultiple: action.atrMultiple,
      takeProfits: action.takeProfits,
      pointValue: pv,
    });
  };

  const settleClosed = () => {
    if (!position || position.status !== 'closed') return;
    const trade = finalizeTrade(position);
    cash += Number(trade.pnl) || 0;
    trades.push(trade);
    position = null;
  };

  const flushPending = (bar, i, { onlyLimitsPlacedThisBar = false } = {}) => {
    const rest = [];
    for (const action of pending) {
      if (onlyLimitsPlacedThisBar) {
        const isNewLimit = action.type === 'enter'
          && (action.orderType || 'market') === 'limit'
          && action.placedIndex === i;
        if (!isNewLimit) {
          rest.push(action);
          continue;
        }
      }
      if (action.type === 'exit' && position) {
        closePosition(position, bar, i, action.reason || 'signal');
        settleClosed();
        continue;
      }
      if (action.type !== 'enter') continue;
      if (pendingEntryExpired(action, session?.[i])) {
        action.expireIndex = i;
        continue;
      }

      const fillPrice = fillPriceFor(action, bar, i);
      if (fillPrice == null) {
        rest.push(action);
        continue;
      }

      const fillQty = resolveFillQty(action, fillPrice);
      const markFilled = () => { action.fillIndex = i; };

      if (!position) {
        // Price tagged the limit but 1 contract exceeds the risk budget — cancel, don't leave a zombie bracket.
        if (fillQty < 1) {
          rejectOrder(action, i, 'insufficient_qty');
          continue;
        }
        markFilled();
        openFromAction(action, bar, i, fillPrice, fillQty);
        continue;
      }

      if (mode === 'skip') {
        rest.push(action);
        continue;
      }

      const opposite = position.side !== action.side;
      if (!opposite) {
        if ((mode === 'add' || mode === 'reduce') && fillQty >= 1) {
          markFilled();
          addToPosition(position, bar, fillQty, fillPrice);
        } else if (fillQty < 1) {
          rejectOrder(action, i, 'insufficient_qty');
        } else {
          rest.push(action);
        }
        continue;
      }

      if (mode === 'add') {
        rest.push(action);
        continue;
      }

      if (mode === 'reduce') {
        if (fillQty < 1) {
          rejectOrder(action, i, 'insufficient_qty');
          continue;
        }
        markFilled();
        reducePosition(position, bar, i, fillQty, 'signal');
        settleClosed();
        continue;
      }

      if (fillQty < 1) {
        rejectOrder(action, i, 'insufficient_qty');
        continue;
      }
      markFilled();
      closePosition(position, bar, i, 'signal');
      settleClosed();
      openFromAction(action, bar, i, fillPrice, fillQty);
    }
    pending = rest;
  };

  for (let i = 0; i < bars.length; i++) {
    const bar = bars[i];
    flushPending(bar, i);

    if (position && position.status !== 'closed') {
      applyBarFills(position, bar, i);
      settleClosed();
    }

    const ctx = {
      i,
      bar,
      bars,
      ind: sliceInd(prepared.ind, i),
      params,
      position: snapshotPosition(position),
      session: session ? session[i] : null,
      state,
      tf,
    };

    const explained = typeof strategy.explain === 'function' ? (strategy.explain(ctx) || []) : [];
    const inSession = !filterEntries || barInTradeSession(bar, tradeSession);
    if (filterEntries) {
      explained.push({
        id: 'trade-session',
        label: inSession ? 'In session' : 'Outside session',
        pass: inSession,
      });
    }
    barCriteria[i] = explained;

    const intents = inSession ? (strategy.onBar(ctx) || []) : [];
    for (const intent of intents) {
      if (!intent || intent.type === 'none') continue;
      if (intent.type === 'updateStops' && position) {
        if (Number.isFinite(intent.stopPrice)) position.stopPrice = intent.stopPrice;
        continue;
      }
      if (intent.type === 'exit') {
        pending.push({ type: 'exit', reason: intent.reason || 'signal' });
        continue;
      }
      if (intent.type === 'enter') {
        const hasPosition = position && position.status !== 'closed';
        if (mode === 'skip' && hasPosition) continue;
        const orderType = intent.orderType || 'market';
        const opposite = hasPosition && position.side !== intent.side;
        if (mode === 'reverse' && orderType === 'market' && opposite) {
          closePosition(position, bar, i, 'signal', bar.close);
          settleClosed();
          const fillPrice = bar.close;
          const fillQty = resolveFillQty(intent, fillPrice);
          if (fillQty < 1) continue;
          openFromAction(intent, bar, i, fillPrice, fillQty);
          continue;
        }
        const order = {
          ...intent,
          id: `w-${nextWorkingId++}`,
          placedIndex: i,
          placedTime: bar.timestamp,
          fillIndex: null,
          expireIndex: null,
        };
        workingOrders.push(order);

        // If risk sizing cannot afford 1 contract at the limit, don't show a fillable bracket.
        if ((order.orderType || 'market') === 'limit' && Number.isFinite(order.limitPrice)) {
          const estQty = resolveFillQty(order, order.limitPrice);
          if (estQty < 1) {
            rejectOrder(order, i, 'insufficient_qty');
            continue;
          }
        }

        pending.push(order);
      }
    }

    // Limits may fill on the signal bar when that bar's range already tagged the price.
    flushPending(bar, i, { onlyLimitsPlacedThisBar: true });
    if (position && position.status !== 'closed') {
      applyBarFills(position, bar, i);
      settleClosed();
    }
  }

  const equity = buildEquity(trades, bars);
  const open = position ? finalizeTrade(position) : null;
  const stats = computeStats(trades, equity, open);
  const skippedOrders = workingOrders.filter((o) => o.rejectReason);

  return {
    bars,
    trades,
    openPosition: open,
    equity,
    stats,
    barCriteria,
    indicators: prepared.ind || {},
    workingOrders,
    skippedOrders,
    params,
    strategyId: strategy.id,
  };
}
